import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedEditor,
  SerializedLexicalNode,
  Spread
} from 'lexical';

import { $applyNodeReplacement, createEditor, DecoratorNode } from 'lexical';
import * as React from 'react';
import { Suspense } from 'react';
import AvatarDefault from '../../../assets/medium_default_optimized.png';

const VideoComponent = React.lazy(() => import('./VideoComponent'));

export interface VideoPayload {
  src: string;
  caption?: LexicalEditor;
  key?: NodeKey;
  width?: number;
  maxWidth?: number;
  height?: number;
  showCaption?: boolean;
  captionsEnabled?: boolean;
  isAddedFromContributorList?: boolean;
  contributors?: any;
  altText?: string;
  type?: string;
}

function $convertVideoElement(domNode: Node): null | DOMConversionOutput {
  const video = domNode as HTMLVideoElement;
  if (video.src.startsWith('file://')) {
    return null;
  }
  const { src, width, height } = video;
  const node = $createVideoNode({ src, width, height, type: 'video' });
  return { node };
}

export type SerializedVideoNode = Spread<
  {
    src: string;
    caption: SerializedEditor;
    width?: number;
    maxWidth: number;
    height?: number;
    showCaption: boolean;
    isAddedFromContributorList?: boolean;
    contributors?: any;
    altText?: string;
  },
  SerializedLexicalNode
>;

export class VideoNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __width: number | 'inherit';
  __maxWidth: number;
  __height: number | 'inherit';
  __showCaption: boolean;
  __caption: LexicalEditor;
  __captionsEnabled: boolean;
  __isAddedFromContributorList: boolean;
  __contributors: any;
  __altText: string;

  static getType(): string {
    return 'video';
  }

  static clone(node: VideoNode): VideoNode {
    return new VideoNode(
      node.__src,
      node.__width,
      node.__maxWidth,
      node.__height,
      node.__showCaption,
      node.__caption,
      node.__captionsEnabled,
      node.__isAddedFromContributorList,
      node.__contributors,
      node.__altText,
      node.__key
    );
  }

  static importJSON(serializedNode: SerializedVideoNode): VideoNode {
    const {
      src,
      width,
      maxWidth,
      height,
      caption,
      showCaption,
      isAddedFromContributorList,
      contributors,
      altText,
      type
    } = serializedNode;
    const node = $createVideoNode({
      src,
      width,
      maxWidth,
      height,
      showCaption,
      isAddedFromContributorList,
      contributors,
      altText,
      type
    });
    const nestedEditor = node.__caption;
    const editorState = nestedEditor.parseEditorState(caption.editorState);
    if (!editorState.isEmpty()) {
      nestedEditor.setEditorState(editorState);
    }
    return node;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('video');
    element.setAttribute('src', this.__src);
    element.setAttribute('width', this.__width.toString());
    element.setAttribute('height', this.__height.toString());
    element.setAttribute('controls', 'true'); // Enable video controls by default
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      video: (node: Node) => ({
        conversion: $convertVideoElement,
        priority: 0
      })
    };
  }

  constructor(
    src: string,
    width?: 'inherit' | number,
    maxWidth?: number,
    height?: 'inherit' | number,
    showCaption?: boolean,
    caption?: LexicalEditor,
    captionsEnabled?: boolean,
    isAddedFromContributorList?: boolean,
    contributors?: any,
    altText?: string,
    key?: NodeKey
  ) {
    super(key);
    this.__src = src;
    this.__width = width || 'inherit';
    this.__maxWidth = maxWidth;
    this.__height = height || 'inherit';
    this.__showCaption = showCaption || false;
    this.__caption =
      caption ||
      createEditor({
        nodes: []
      });
    this.__contributors = contributors;
    this.__isAddedFromContributorList = isAddedFromContributorList || false;
    this.__altText = altText || '';
    this.__captionsEnabled = captionsEnabled || captionsEnabled === undefined;
  }

  exportJSON(): SerializedVideoNode {
    return {
      src: this.__src,
      caption: this.__caption.toJSON(),
      maxWidth: this.__maxWidth,
      width: this.__width === 'inherit' ? 0 : this.__width,
      height: this.__height === 'inherit' ? 0 : this.__height,
      showCaption: this.__showCaption,
      isAddedFromContributorList: this.__isAddedFromContributorList,
      contributors: this.__contributors,
      altText: this.__altText,
      type: 'video',
      version: 1
    };
  }

  setWidthAndHeight(width: 'inherit' | number, height: 'inherit' | number): void {
    const writable = this.getWritable();
    writable.__width = width;
    writable.__height = height;
  }

  setShowCaption(showCaption: boolean): void {
    const writable = this.getWritable();
    writable.__showCaption = showCaption;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    const theme = config.theme;
    const className = theme.media;
    if (className !== undefined) {
      span.className = className;
    }
    return span;
  }

  updateDOM(): false {
    return false;
  }

  getSrc(): string {
    return this.__src;
  }

  getContributorId() {
    return this.__contributors?.id || null;
  }

  decorate(): JSX.Element {
    return (
      <Suspense fallback={null}>
        <VideoComponent
          src={this.__src}
          width={this.__width}
          altText={this.__altText}
          maxWidth={this.__maxWidth}
          height={this.__height}
          showCaption={this.__showCaption}
          nodeKey={this.getKey()}
          caption={this.__caption}
          isAddedFromContributorList={this.__isAddedFromContributorList}
          resizable={true}
          contributors={this.__contributors}
        />

        {this.__isAddedFromContributorList && (
          <div className="flex flex-wrap items-center justify-center gap-2 mt-[10px] font-ui text-base">
            <p className="text-dim-gray font-[600]">{this.__altText}</p>
            <div className="flex items-center gap-2">
              <span className="text-dim-gray font-[600] whitespace-nowrap">📷 Video By:</span>
              <div
                className="flex items-start gap-2 text-jet-black font-[600] cursor-pointer"
                onClick={() => window.open(`/${this.__contributors.username}`)}
              >
                <img
                  className="w-6 h-6 object-cover rounded-full flex-shrink-0"
                  src={this.__contributors?.imgUrl || AvatarDefault}
                  alt=""
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    title={this.__contributors?.firstName}
                    className="hover:text-growth-green transition-all max-w-[100px] truncate duration-200"
                  >
                    {this.__contributors?.firstName}
                  </span>
                  {this.__contributors?.username && (
                    <span className="font-[600] text-xs text-02-dim-gray whitespace-nowrap">
                      @{this.__contributors?.username}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Suspense>
    );
  }
}

export function $createVideoNode({
  src,
  width,
  maxWidth,
  height,
  showCaption,
  caption,
  captionsEnabled,
  isAddedFromContributorList,
  contributors,
  altText,
  key
}: VideoPayload): VideoNode {
  return $applyNodeReplacement(
    new VideoNode(
      src,
      width,
      maxWidth,
      height,
      showCaption,
      caption,
      captionsEnabled,
      isAddedFromContributorList,
      contributors,
      altText,
      key
    )
  );
}

export function $isVideoNode(node: LexicalNode | null | undefined): node is VideoNode {
  return node instanceof VideoNode;
}
